import React from 'react';

interface NoDataFoundProps {
  title?: string;
  message?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

const NoDataFound: React.FC<NoDataFoundProps> = ({
  title = 'No Results Found',
  message = 'There are no records to display at the moment.',
  action,
}) => (
  <div className="ndf">
    <div className="ndf__scene">
      {/* ── Glow orbs ── */}
      <span className="ndf__orb ndf__orb--1" />
      <span className="ndf__orb ndf__orb--2" />

      {/* ── Floating illustration ── */}
      <div className="ndf__float">
        <svg
          viewBox="0 0 220 200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="ndf__svg"
          aria-hidden="true"
        >
          {/* Shadow under stack */}
          <ellipse cx="110" cy="185" rx="60" ry="8" className="ndf-fill-brand-hover" opacity="0.10" />

          {/* ── Back document ── */}
          <g transform="translate(68,32) rotate(-8, 55, 80)">
            <rect x="0" y="0" width="88" height="112" rx="8" className="ndf-fill-brand-subtle" opacity="0.7" />
            <rect x="12" y="20" width="40" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.3" />
            <rect x="12" y="32" width="60" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.3" />
            <rect x="12" y="44" width="52" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.3" />
            <rect x="12" y="56" width="44" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.3" />
            <rect x="12" y="68" width="56" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.3" />
          </g>

          {/* ── Mid document ── */}
          <g transform="translate(54,22) rotate(-3, 60, 90)">
            <rect x="0" y="0" width="92" height="118" rx="8" className="ndf-fill-brand-subtle ndf-stroke-brand" strokeWidth="1" opacity="0.6" />
            <rect x="12" y="20" width="46" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.5" />
            <rect x="12" y="32" width="64" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.2" />
            <rect x="12" y="44" width="54" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.2" />
            <rect x="12" y="56" width="48" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.2" />
            <rect x="12" y="68" width="60" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.2" />
            <rect x="12" y="80" width="36" height="5" rx="2.5" className="ndf-fill-brand" opacity="0.2" />
          </g>

          {/* ── Front document ── */}
          <rect x="42" y="14" width="100" height="130" rx="10"
            fill="white" className="ndf-stroke-subtle" strokeWidth="1.5"
            filter="url(#doc-shadow)" />
          {/* Doc header stripe */}
          <rect x="42" y="14" width="100" height="28" rx="10" className="ndf-fill-brand" />
          <rect x="42" y="32" width="100" height="10" className="ndf-fill-brand" />
          {/* Header lines */}
          <rect x="54" y="22" width="36" height="5" rx="2.5" fill="white" opacity="0.5" />
          <rect x="96" y="22" width="16" height="5" rx="2.5" fill="white" opacity="0.3" />
          {/* Body lines */}
          <rect x="54" y="54" width="42" height="5" rx="2.5" className="ndf-fill-brand-subtle" />
          <rect x="54" y="66" width="72" height="5" rx="2.5" className="ndf-fill-brand-subtle" opacity="0.6" />
          <rect x="54" y="78" width="60" height="5" rx="2.5" className="ndf-fill-brand-subtle" opacity="0.6" />
          <rect x="54" y="90" width="68" height="5" rx="2.5" className="ndf-fill-brand-subtle" opacity="0.6" />
          <rect x="54" y="102" width="48" height="5" rx="2.5" className="ndf-fill-brand-subtle" opacity="0.6" />
          {/* Empty state bar */}
          <rect x="54" y="118" width="76" height="14" rx="4" className="ndf-fill-brand-subtle ndf-stroke-brand" strokeWidth="1" opacity="0.4" />
          <rect x="58" y="122" width="26" height="6" rx="3" className="ndf-fill-brand" opacity="0.4" />

          {/* ── Magnifier ── */}
          <circle cx="148" cy="118" r="30" fill="white" filter="url(#mag-shadow)" />
          <circle cx="148" cy="118" r="26" className="ndf-fill-brand-subtle" opacity="0.5" />
          <circle cx="148" cy="118" r="26" fill="none" className="ndf-stroke-brand" strokeWidth="3" />
          <circle cx="148" cy="118" r="18" fill="white" className="ndf-stroke-subtle" strokeWidth="1" />
          <circle cx="148" cy="114" r="6" className="ndf-fill-brand-subtle" opacity="0.7" />
          <path d="M144 118 Q148 111, 152 118" className="ndf-stroke-brand" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.6" />
          {/* Handle */}
          <line x1="168" y1="138" x2="180" y2="152" className="ndf-stroke-brand" strokeWidth="4.5" strokeLinecap="round" />
          <line x1="168" y1="138" x2="180" y2="152" className="ndf-stroke-subtle" strokeWidth="2.5" strokeLinecap="round" />

          {/* ── Sparkles ── */}
          <g transform="translate(172, 40)">
            <line x1="0" y1="-7" x2="0" y2="7" className="ndf-stroke-brand" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="-7" y1="0" x2="7" y2="0" className="ndf-stroke-brand" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="-5" y1="-5" x2="5" y2="5" className="ndf-stroke-brand" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
            <line x1="5" y1="-5" x2="-5" y2="5" className="ndf-stroke-brand" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
          </g>
          <g transform="translate(22, 148)">
            <line x1="0" y1="-5" x2="0" y2="5" className="ndf-stroke-brand" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
            <line x1="-5" y1="0" x2="5" y2="0" className="ndf-stroke-brand" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
          </g>
          {/* dot accents */}
          <circle cx="30" cy="50" r="3.5" className="ndf-fill-brand-subtle" />
          <circle cx="195" cy="80" r="2.5" className="ndf-fill-brand" opacity="0.4" />
          <circle cx="185" cy="165" r="4" className="ndf-fill-brand-subtle ndf-stroke-brand" strokeWidth="1" opacity="0.5" />
          <circle cx="18" cy="110" r="3" className="ndf-fill-brand-subtle" opacity="0.8" />

          {/* ── Filters ── */}
          <defs>
            <filter id="doc-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="var(--brand-hover)" floodOpacity="0.14" />
            </filter>
            <filter id="mag-shadow" x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="var(--brand-hover)" floodOpacity="0.20" />
            </filter>
          </defs>
        </svg>
      </div>

      {/* ── Floating dots ring ── */}
      <div className="ndf__dot-ring">
        <span className="ndf__dot ndf__dot--1" />
        <span className="ndf__dot ndf__dot--2" />
        <span className="ndf__dot ndf__dot--3" />
        <span className="ndf__dot ndf__dot--4" />
        <span className="ndf__dot ndf__dot--5" />
      </div>
    </div>

    <p className="ndf__title">{title}</p>
    <p className="ndf__message">{message}</p>
    {action && <div className="ndf__action">{action}</div>}
  </div>
);

export default NoDataFound;
